import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
  Tooltip,
  Stack,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import AdminLayout from '../../layouts/AdminLayout';
import { AdminService } from '../../services/adminService';

// Audience des pages enseignants du site, usage des deux générateurs publics, et ce
// que la dernière publication des pages indexables (site/exercices/) a rendu public.
// Trois sources de nature différente : cf. backend/routes/admin.teacherPages.routes.js.

const FENETRES = [7, 30, 90];
const SITE = 'https://www.dicto.fr';

// Libellés des pages fixes ; une page /exercices/<slug> prend celui du manifeste.
const PAGES_FIXES = {
  '/enseignants': 'Accueil enseignants',
  '/enseignant': 'Générateur de fiches d\'exercices',
  '/lecons': 'Générateur de fiches de leçon',
  '/en-classe': 'En classe',
  '/programme': 'Programme',
  '/exercices': 'Leçons et exercices — index',
};
const OUTILS = { fiche: 'Fiche d\'exercices', lecon: 'Fiche de leçon' };

const fmtNum = (n) => (n ?? 0).toLocaleString('fr-FR');
function fmtDate(v) {
  if (!v) return '—';
  try { return new Date(v).toLocaleString('fr-FR'); } catch { return String(v); }
}
const cheminDuSlug = (slug) => `/exercices/${String(slug).replace(/\.html$/, '')}`;

function Kpi({ label, value, sub }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="h4">{fmtNum(value)}</Typography>
        {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
      </CardContent>
    </Card>
  );
}

export default function TeacherPagesScreen() {
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const charger = useCallback(async (d) => {
    setLoading(true);
    setError(null);
    try {
      setData(await AdminService.getTeacherPages(d));
    } catch (e) {
      setError(e?.message || 'Erreur lors du chargement des pages enseignants');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { charger(days); }, [days, charger]);

  const pagesPubliees = useMemo(() => {
    const pages = data?.publication?.manifeste?.pages || [];
    return [...pages].sort((a, b) =>
      String(a.competenceLabel).localeCompare(String(b.competenceLabel), 'fr') || a.cycle - b.cycle);
  }, [data]);
  const pageSeoParChemin = useMemo(
    () => new Map(pagesPubliees.map((p) => [cheminDuSlug(p.slug), p])),
    [pagesPubliees],
  );
  const vuesParChemin = useMemo(() => new Map((data?.pages || []).map((p) => [p.page, p])), [data]);
  const retires = useMemo(
    () => new Map((data?.exercicesRetires || []).map((e) => [String(e.id), e.raisons.join(', ')])),
    [data],
  );

  const libellePage = (chemin) => {
    if (PAGES_FIXES[chemin]) return PAGES_FIXES[chemin];
    const p = pageSeoParChemin.get(chemin);
    return p ? `${p.competenceLabel} — cycle ${p.cycle}` : chemin;
  };

  const totaux = useMemo(() => {
    const pages = data?.pages || [];
    const outils = data?.outils || [];
    const somme = (liste, cle) => liste.reduce((t, r) => t + (r[cle] || 0), 0);
    return {
      vues: somme(pages, 'vues'),
      depuisMoteur: somme(pages, 'depuisMoteur'),
      fiches: somme(outils.filter((o) => o.outil === 'fiche'), 'servies'),
      lecons: somme(outils.filter((o) => o.outil === 'lecon'), 'servies'),
      refusees: somme(outils, 'refusees'),
    };
  }, [data]);

  const publication = data?.publication;
  const ecartees = publication?.manifeste?.ecartees || [];

  return (
    <AdminLayout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Pages enseignants</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 900 }}>
          Audience des pages du site destinées aux enseignants, usage des deux générateurs
          (fiches d'exercices, fiches de leçon) et contenu des pages « leçons et exercices »
          actuellement en ligne.
        </Typography>

        <ToggleButtonGroup
          size="small"
          exclusive
          value={days}
          onChange={(_e, v) => { if (v != null) setDays(v); }}
          sx={{ mb: 2 }}
        >
          {FENETRES.map((d) => <ToggleButton key={d} value={d}>{d} j</ToggleButton>)}
        </ToggleButtonGroup>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {data?.tablesManquantes?.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Migration 156 non appliquée : {data.tablesManquantes.join(', ')} absente(s). Les
            vues de pages restent affichées.
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : data && (
          <>
            <Box sx={{ display: 'grid', gap: 2, mb: 3, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
              <Kpi label="Vues des pages enseignants" value={totaux.vues} sub={`sur ${days} jours`} />
              <Kpi label="Arrivées depuis un moteur" value={totaux.depuisMoteur} sub="Google, Bing, Qwant…" />
              <Kpi label="Fiches d'exercices servies" value={totaux.fiches} />
              <Kpi label="Fiches de leçon servies" value={totaux.lecons} />
              <Kpi label="Demandes non servies" value={totaux.refusees} sub="notion sans couverture ou sans leçon" />
            </Box>

            {/* ── Audience par page ─────────────────────────────────────────── */}
            <Typography variant="h6" gutterBottom>Audience par page</Typography>
            <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1, maxWidth: 900 }}>
              Vues émises par le site (quest.js) : seuls les visiteurs qui ont répondu à la
              bannière cookies sont comptés, et « visiteurs » ne compte que ceux qui ont accepté
              la mesure d'audience. Pour les impressions et positions Google : Search Console.
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3, maxHeight: 480 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Page</TableCell>
                    <TableCell align="right">Vues</TableCell>
                    <TableCell align="right">Visiteurs (consentis)</TableCell>
                    <TableCell align="right">Depuis un moteur</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.pages.length === 0 ? (
                    <TableRow><TableCell colSpan={4}>
                      <Typography color="text.secondary">Aucune vue enregistrée sur la période.</Typography>
                    </TableCell></TableRow>
                  ) : data.pages.map((p) => (
                    <TableRow key={p.page} hover>
                      <TableCell>
                        <Link href={`${SITE}${p.page}`} target="_blank" rel="noopener noreferrer">
                          {libellePage(p.page)}
                        </Link>
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          {p.page}
                        </Typography>
                      </TableCell>
                      <TableCell align="right"><strong>{fmtNum(p.vues)}</strong></TableCell>
                      <TableCell align="right">{fmtNum(p.visiteurs)}</TableCell>
                      <TableCell align="right">{fmtNum(p.depuisMoteur)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* ── Générateurs ───────────────────────────────────────────────── */}
            <Typography variant="h6" gutterBottom>Fiches générées par notion</Typography>
            <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1, maxWidth: 900 }}>
              Compteur serveur, sans donnée personnelle : il compte tous les enseignants. Une
              demande non servie est une notion qu'un enseignant voulait et que la base ne
              couvre pas (ou dont la leçon manque).
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3, maxHeight: 480 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Outil</TableCell>
                    <TableCell>Notion</TableCell>
                    <TableCell>Niveaux demandés</TableCell>
                    <TableCell align="right">Servies</TableCell>
                    <TableCell align="right">Non servies</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.outils.length === 0 ? (
                    <TableRow><TableCell colSpan={5}>
                      <Typography color="text.secondary">Aucune fiche générée sur la période.</Typography>
                    </TableCell></TableRow>
                  ) : data.outils.map((o) => (
                    <TableRow key={`${o.outil}|${o.notionId}`} hover>
                      <TableCell><Chip size="small" label={OUTILS[o.outil] || o.outil} /></TableCell>
                      <TableCell>
                        {o.notionLabel}
                        {o.notionLabel !== o.notionId && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                            ({o.notionId})
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{(o.niveaux || []).join(', ')}</TableCell>
                      <TableCell align="right"><strong>{fmtNum(o.servies)}</strong></TableCell>
                      <TableCell align="right">
                        {o.refusees > 0 ? <Chip size="small" color="warning" label={fmtNum(o.refusees)} /> : 0}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* ── Pages publiées ────────────────────────────────────────────── */}
            <Typography variant="h6" gutterBottom>Pages « leçons et exercices » en ligne</Typography>
            {!publication ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                Aucune publication enregistrée. Publier avec <code>./scripts/publier_pages_seo.sh</code> :
                le manifeste est enregistré après la mise en ligne.
              </Alert>
            ) : (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Publiée le <strong>{fmtDate(publication.publieeLe)}</strong> — {fmtNum(publication.nbPages)} pages,{' '}
                  {fmtNum(publication.nbSections)} sections, {fmtNum(publication.nbItems)} items d'exercice publics.
                </Typography>
                {data.exercicesRetires.length > 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {data.exercicesRetires.length} exercice(s) publié(s) ne seraient plus servis aujourd'hui
                    (désactivés, signalés ou dévalidés) : ils restent en ligne jusqu'à la prochaine
                    publication. Marqués en rouge ci-dessous.
                  </Alert>
                )}

                {pagesPubliees.map((p) => {
                  const chemin = cheminDuSlug(p.slug);
                  const vues = vuesParChemin.get(chemin);
                  const nbRetires = p.sections
                    .flatMap((s) => s.exercices || [])
                    .filter((id) => retires.has(String(id))).length;
                  return (
                    <Accordion key={p.slug} disableGutters>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Typography sx={{ minWidth: 260 }}>{p.competenceLabel} — cycle {p.cycle}</Typography>
                          <Chip size="small" label={`${p.sections.length} section(s)`} />
                          <Chip size="small" variant="outlined" label={`${fmtNum(vues?.vues)} vues`} />
                          {nbRetires > 0 && <Chip size="small" color="error" label={`${nbRetires} à retirer`} />}
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Link href={`${SITE}${chemin}`} target="_blank" rel="noopener noreferrer" variant="body2">
                          {SITE}{chemin}
                        </Link>
                        <Table size="small" sx={{ mt: 1 }}>
                          <TableHead>
                            <TableRow>
                              <TableCell>Notion</TableCell>
                              <TableCell>Section</TableCell>
                              <TableCell align="right">Leçon (car.)</TableCell>
                              <TableCell align="right">Items</TableCell>
                              <TableCell>Exercices publiés</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {p.sections.map((s) => (
                              <TableRow key={s.notionId}>
                                <TableCell>
                                  <Link href={`${SITE}${chemin}${s.ancre ? `#${s.ancre}` : ''}`} target="_blank" rel="noopener noreferrer">
                                    {s.notionLabel}
                                  </Link>
                                </TableCell>
                                <TableCell>{s.genre === 'introduction' ? 'introduction' : 'approfondissement'}</TableCell>
                                <TableCell align="right">{fmtNum(s.leconCaracteres)}</TableCell>
                                <TableCell align="right">{fmtNum(s.items)}</TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {(s.exercices || []).length === 0 && (
                                      <Typography variant="caption" color="text.secondary">
                                        — (rappel : spécimen sur la page d'introduction)
                                      </Typography>
                                    )}
                                    {(s.exercices || []).map((id) => {
                                      const raison = retires.get(String(id));
                                      return (
                                        <Tooltip key={id} title={raison || 'Ouvrir dans l\'éditeur'}>
                                          <Chip
                                            size="small"
                                            label={`#${id}`}
                                            color={raison ? 'error' : 'default'}
                                            clickable
                                            onClick={() => navigate(`/admin/exercises/view/${id}`)}
                                          />
                                        </Tooltip>
                                      );
                                    })}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}

                {ecartees.length > 0 && (
                  <Accordion disableGutters sx={{ mt: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>Notions non publiées ({ecartees.length})</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Table size="small">
                        <TableHead>
                          <TableRow><TableCell>Notion</TableCell><TableCell>Raison</TableCell></TableRow>
                        </TableHead>
                        <TableBody>
                          {ecartees.map((e) => (
                            <TableRow key={e.notionId}>
                              <TableCell>{e.notionId}</TableCell>
                              <TableCell>{e.raison}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </AccordionDetails>
                  </Accordion>
                )}

                {data.historique.length > 1 && (
                  <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 2 }}>
                    Publications précédentes :{' '}
                    {data.historique.slice(1).map((h) => `${fmtDate(h.publieeLe)} (${h.nbPages} p.)`).join(' · ')}
                  </Typography>
                )}
              </>
            )}
          </>
        )}
      </Box>
    </AdminLayout>
  );
}
